import { computed, inject, provide, toValue } from "vue";
import { logEvent } from "~/utils/firebase";
import {
	getConsultationCountBucket,
	getMentorTypeByAreaType,
	getPriceBucket,
	normalizeMentorRating,
} from "@variants/seer/master-fortune/composables/useMentorListTracking";

export const MENTOR_DETAIL_TRACKING_KEY = "mentorDetailTracking";

const PAGE_NAME = "mentor_detail";

const TAB_NAME_MAP = {
	consult: "consult_service",
	solution: "solution",
	media: "media",
	evaluate: "user_review",
	services: "master_service",
};

function getEntrySource() {
	if (!import.meta.client) return "direct";
	const referrer = document.referrer;
	if (!referrer) return "direct";
	try {
		const referrerUrl = new URL(referrer);
		if (referrerUrl.host === window.location.host) return "internal";
		return referrerUrl.host;
	} catch {
		return "unknown";
	}
}

function cleanParams(params) {
	return Object.fromEntries(
		Object.entries(params).filter(
			([, value]) => value !== undefined && value !== null && value !== ""
		)
	);
}

export function resolveMentorDetailTabName(key) {
	return TAB_NAME_MAP[key] || key || "unknown";
}

export function resolveMediaTypeByItem(item) {
	if (!item) return "video";
	if (item.kind === "article" || item.isVideo === false) return "article";
	return "video";
}

export function getProfileCompleteness(info = {}) {
	const checks = [
		Boolean(info.avatar),
		Boolean(info.nickname || info.nickname_en),
		Boolean(info.introduce || info.introduction || info.introduce_en),
		Boolean(info.year),
		Boolean(info.answers),
		Boolean(info.score),
		Boolean(info.tags || info.academic_sector || info.label),
	];
	const filled = checks.filter(Boolean).length;
	if (filled <= 2) return "low";
	if (filled <= 5) return "medium";
	return "high";
}

/**
 * 导师详情曝光：进入可视区域 50% 即触发，同一 key 去重。
 */
export function createMentorDetailImpressionTracker(track) {
	const impressedKeys = new Set();
	let observer = null;

	const ensureObserver = () => {
		if (observer || !import.meta.client) return;
		observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					const key = entry.target.dataset.mentorDetailImpressionKey;
					if (!key || impressedKeys.has(key)) return;
					if (!(entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;

					impressedKeys.add(key);
					let eventName = "";
					let payload = {};
					const raw = entry.target.dataset.mentorDetailImpressionParams;
					if (raw) {
						try {
							const parsed = JSON.parse(raw);
							eventName = parsed.event_name || "";
							payload = parsed.payload || {};
						} catch {
							eventName = "";
							payload = {};
						}
					}
					if (eventName) track(eventName, payload);
					observer?.unobserve(entry.target);
				});
			},
			{ threshold: [0.5] }
		);
	};

	const bind = (el, impressionKey, eventName, payload = {}) => {
		if (!el || !impressionKey || !eventName || !import.meta.client) return;
		ensureObserver();
		if (impressedKeys.has(impressionKey)) return;

		el.dataset.mentorDetailImpressionKey = impressionKey;
		el.dataset.mentorDetailImpressionParams = JSON.stringify({
			event_name: eventName,
			payload,
		});
		observer.observe(el);
	};

	const reset = () => {
		impressedKeys.clear();
		observer?.disconnect();
		observer = null;
	};

	return { bind, reset };
}

export function useMentorDetailTracking(options = {}) {
	const deviceType = options.deviceType || "pc";

	const mentorId = computed(() => {
		const id = toValue(options.mentorId);
		return id != null && id !== "" ? String(id) : "";
	});

	const mentorType = computed(() => {
		const explicit = toValue(options.mentorType);
		if (explicit === "famous" || explicit === "normal") return explicit;
		return getMentorTypeByAreaType(toValue(options.areaType));
	});

	const baseParams = computed(() =>
		cleanParams({
			page_name: PAGE_NAME,
			mentor_id: mentorId.value,
			mentor_type: mentorType.value,
			entry_source: getEntrySource(),
			device_type: deviceType,
		})
	);

	function track(eventName, params = {}) {
		logEvent(eventName, cleanParams({ ...baseParams.value, ...params }));
	}

	return {
		track,
		baseParams,
		mentorId,
		mentorType,
		getPriceBucket,
		getConsultationCountBucket,
		normalizeMentorRating,
		getProfileCompleteness,
		createImpressionTracker: () => createMentorDetailImpressionTracker(track),
	};
}

export function provideMentorDetailTracking(tracking) {
	provide(MENTOR_DETAIL_TRACKING_KEY, tracking);
	return tracking;
}

export function useInjectedMentorDetailTracking() {
	return inject(MENTOR_DETAIL_TRACKING_KEY, null);
}
